import { createApi } from "@reduxjs/toolkit/query/react";
import {
  ITEToken,
  MappingResponse,
  TCurrentUser,
  TOrganization,
  TOrganizationPublic,
} from "@timeedit/registration-schemas";
import { z } from "zod";
import { baseQuery } from "../../baseQuery";

interface FetchOrganizationParams {
  organizationId: string | null | undefined;
}

/**
 * Cache shape for `fetchTeUser`: token fields first, user payload overrides
 * on overlap (e.g. email). Shared with cache-seeding so seeded entries are
 * indistinguishable from fetched ones to downstream consumers.
 */
export const mergeTokenAndUser = (
  token: ITEToken,
  user: TCurrentUser
): TCurrentUser & ITEToken => ({ ...token, ...user });

export const teApiSlice = createApi({
  reducerPath: "teApi",
  baseQuery,
  tagTypes: [
    "Organizations",
    "Users",
    "Mapping",
    "Objects",
    "Types",
    "Fields",
    "MemberTypes",
  ],
  endpoints(builder) {
    return {
      fetchTeOrganization: builder.query<
        TOrganizationPublic,
        FetchOrganizationParams
      >({
        query({ organizationId }) {
          return {
            url: `/organizations/${organizationId}`,
            method: "GET",
          };
        },
        extraOptions: {
          validation: TOrganization,
        },
        providesTags: ["Organizations"],
      }),
      fetchTeUser: builder.query<TCurrentUser & ITEToken, ITEToken>({
        query() {
          return {
            url: `/user`,
            method: "GET",
          };
        },
        extraOptions: {
          // Cache only carries the current-user identity subset plus the
          // token. Other TTEUser fields are either redundant with the token
          // or unused.
          validation: z.object({
            ...TCurrentUser.shape,
            ...ITEToken.shape,
          }),
        },
        transformResponse: (response: TCurrentUser, _, arg: ITEToken) =>
          mergeTokenAndUser(arg, response),
        providesTags: ["Users"],
      }),
      fetchMappingStore: builder.query<MappingResponse, void>({
        query() {
          return {
            url: "/mapping-store",
            method: "GET",
            header: {
              "content-type": "application/json; charset=UTF-8",
            },
          };
        },
        extraOptions: {
          validation: MappingResponse.refine(
            (schema) =>
              // memberTypes can have a 0 length value. So it is okay if that is empty.
              schema.mappingData.objectTypes.length > 0 ||
              schema.fields.length > 0 ||
              schema.types.length > 0,
            `Mapping store has empty data. Fields, Types and mapping data is required. `
          ),
        },
        providesTags: ["Mapping"],
      }),
    };
  },
});

export const {
  useFetchTeOrganizationQuery,
  useLazyFetchTeOrganizationQuery,
  useFetchTeUserQuery,
  useLazyFetchTeUserQuery,
  useFetchMappingStoreQuery,
} = teApiSlice;
